// Shows an invoice inside the app: the stored PDF that went to the parent (Storage for portal invoices, Drive for
// the old generator's), and a live A4 preview rendered from the same template the PDFs are printed from.

import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, useMediaQuery } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import { loadStoredPdf, today, type PreschoolInvoice } from "../../shared/preschoolInvoices";
import {
  renderPreschoolInvoiceHtml,
  renderPreschoolReceiptHtml,
} from "../../../../preschool.bee-happy.club_functions/functions/src/preschool/preschoolDocuments";

type View = "stored" | "preview" | "receipt";

export const PdfDialog: React.FC<{ invoice: PreschoolInvoice | null; title?: string; onClose: () => void }> = ({ invoice, title, onClose }) => {
  const fullScreen = useMediaQuery("(max-width: 900px)");
  const hasStored = !!invoice && (!!invoice.pdfPath || !!invoice.drivePdfId);
  const [view, setView] = useState<View>("preview");
  const [storedUrl, setStoredUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    setView(hasStored ? "stored" : "preview");
    setStoredUrl(null);
    setError(null);
  }, [invoice?.ref, invoice?.pdfPath]);

  useEffect(() => {
    if (!invoice || view !== "stored" || !invoice.pdfPath || storedUrl) return;
    setLoading(true);
    loadStoredPdf(invoice.ref)
      .then((url) => (url ? setStoredUrl(url) : setError("Stored PDFs are not available in dev mode; see Preview.")))
      .catch((e) => setError(`Could not load the PDF: ${e instanceof Error ? e.message : String(e)}`))
      .finally(() => setLoading(false));
  }, [invoice, view, storedUrl]);

  useEffect(() => () => { if (storedUrl) URL.revokeObjectURL(storedUrl); }, [storedUrl]);

  if (!invoice) return null;

  const page = view === "receipt" && invoice.receipt
    ? renderPreschoolReceiptHtml(invoice, invoice.receipt.paymentDate, invoice.receipt.createdAt.slice(0, 10))
    : renderPreschoolInvoiceHtml({ ...invoice, issuedDate: invoice.issuedDate || today(), dueDate: invoice.dueDate || today() });
  // The PDF's margins come from @page, which only applies when printing; show the same margins on screen.
  const html = page.replace("</head>", "<style>@media screen { body { padding: 18mm 20mm; } }</style></head>");
  const driveUrl = invoice.drivePdfId ? `https://drive.google.com/file/d/${invoice.drivePdfId}` : null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 0 }}>{title ?? `${invoice.ref} · ${invoice.childName}`}</DialogTitle>
      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ px: 3 }}>
        {hasStored && <Tab value="stored" label={invoice.pdfPath ? "PDF sent" : "PDF in Drive"} />}
        <Tab value="preview" label={hasStored ? "Preview (current data)" : "Preview"} />
        {invoice.receipt && <Tab value="receipt" label="Receipt" />}
      </Tabs>
      <DialogContent dividers sx={{ p: 0, bgcolor: "grey.200", height: { xs: "100%", md: "78vh" } }}>
        {view === "stored" && (
          <Box sx={{ height: "100%" }}>
            {loading && <Stack alignItems="center" sx={{ pt: 8 }}><CircularProgress /></Stack>}
            {error && <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>}
            {storedUrl && <iframe title="Invoice PDF" src={storedUrl} style={{ border: 0, width: "100%", height: "100%" }} />}
            {!invoice.pdfPath && driveUrl && (
              <iframe title="Invoice PDF in Drive" src={`${driveUrl}/preview`} style={{ border: 0, width: "100%", height: "100%" }} />
            )}
          </Box>
        )}
        {view !== "stored" && (
          <Box sx={{ height: "100%", overflow: "auto", py: 2 }}>
            <Box sx={{ width: "210mm", maxWidth: "100%", minHeight: "297mm", mx: "auto", bgcolor: "#fff", boxShadow: 3 }}>
              <iframe ref={frame} title="Invoice preview" srcDoc={html}
                style={{ border: 0, width: "100%", height: "297mm", display: "block" }} />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {driveUrl && <Button startIcon={<OpenInNewIcon />} href={`${driveUrl}/view`} target="_blank">Open in Drive</Button>}
        {storedUrl && view === "stored" && <Button startIcon={<OpenInNewIcon />} href={storedUrl} target="_blank">Open PDF</Button>}
        {view !== "stored" && (
          <Button startIcon={<PrintIcon />} onClick={() => frame.current?.contentWindow?.print()}>Print / save as PDF</Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
